/**
 * 開発環境専用の「通信失敗シミュレーター」。
 *
 * 目的：公開前に、通信が失敗したときの画面（保存IDが消えない・再試行で戻る）を
 * 実際に操作して確認できるようにする。
 *
 * 重要な前提：
 *  - 本番ビルドでは import.meta.env.DEV が false になるため、必ず「失敗しない」を返す。
 *  - 失敗を注入するのは MockCatalogRepository だけで、HTTPリポジトリには一切影響しない。
 *  - 保存データ（お気に入り等）を書き換える処理はここに置かない。
 *    「取得に失敗する」ことだけを再現する。
 *
 * 使い方：開発中に画面右下の「DEV」ボタンを開き、対象と失敗の種類を選ぶ。
 * 失敗は「次に読み込むとき」だけ発生し、再試行すると正常に戻る。
 * 連続して確認したいときは「もう一度失敗させる」を押す。
 */

export type FailureTarget = "search" | "saved" | "detail" | "genres";

export type FailureMode = "all" | "partial";

export interface FailureTargetInfo {
  id: FailureTarget;
  label: string;
  note: string;
  icon: string;
}

export const FAILURE_TARGETS: FailureTargetInfo[] = [
  {
    id: "saved",
    label: "お気に入り・あとで見る・比較",
    note: "保存リストの一括取得。一部失敗では取得できた作品だけ表示します。",
    icon: "ri-heart-line",
  },
  { id: "search", label: "検索・検索候補", note: "検索結果とキーワード候補の取得。", icon: "ri-search-line" },
  { id: "detail", label: "作品詳細", note: "作品1件と関連作品の取得。", icon: "ri-file-text-line" },
  { id: "genres", label: "ジャンル・カテゴリ", note: "ジャンル一覧とカテゴリ情報の取得。", icon: "ri-layout-grid-line" },
];

export interface DevFailureState {
  mode: FailureMode;
  /** 対象ごとの「あと何回失敗させるか」。0 または未登録なら失敗しない。 */
  armed: Partial<Record<FailureTarget, number>>;
}

export interface FailureDecision {
  active: boolean;
  /** 操作全体をエラーにする */
  all: boolean;
  /** 一部のIDだけ取得失敗させる（保存リストの一括取得のみ） */
  partial: boolean;
}

export const NO_FAILURE: FailureDecision = { active: false, all: false, partial: false };

/** 失敗を有効化したときに仕込む回数。
 * React の StrictMode（開発時）はマウント時に取得処理を2回実行するため、
 * 1回だけにすると「本物のリクエスト」が成功して失敗が再現されない。
 * 2回にしておくと、初回表示は確実に失敗し、再試行（1回）では正常に戻る。 */
const FAILURE_TIMES = 2;

const isDev = (): boolean => {
  try {
    // 本番ビルドでは false に置き換わる（この関数は常に「失敗しない」を返す）
    return Boolean(import.meta.env.DEV);
  } catch {
    return false;
  }
};

let state: DevFailureState = { mode: "all", armed: {} };
let snapshot: DevFailureState = state;
const listeners = new Set<() => void>();

function publish(): void {
  snapshot = { mode: state.mode, armed: { ...state.armed } };
  listeners.forEach((listener) => listener());
}

export function subscribeDevFailure(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** useSyncExternalStore 用（毎回同じ参照を返す） */
export function getDevFailureSnapshot(): DevFailureState {
  return snapshot;
}

export function isDevFailureSupported(): boolean {
  return isDev();
}

/** 指定した対象を「次の取得で失敗」させる */
export function armDevFailure(mode: FailureMode, targets: FailureTarget[]): void {
  if (!isDev() || targets.length === 0) return;
  const armed: Partial<Record<FailureTarget, number>> = {};
  targets.forEach((target) => {
    armed[target] = FAILURE_TIMES;
  });
  state = { mode, armed };
  publish();
}

/** 失敗を解除して正常な状態に戻す */
export function clearDevFailure(): void {
  state = { mode: state.mode, armed: {} };
  publish();
}

/**
 * 取得処理の直前で呼ぶ。失敗させる場合は decision を返し、
 * 残り回数を減らす（0になった対象は自動で解除）。
 */
export function checkDevFailure(target: FailureTarget): FailureDecision {
  if (!isDev()) return NO_FAILURE;
  const remaining = state.armed[target];
  if (!remaining || remaining <= 0) return NO_FAILURE;

  const mode = state.mode;
  const armed = { ...state.armed };
  if (remaining <= 1) delete armed[target];
  else armed[target] = remaining - 1;
  state = { mode, armed };
  publish();

  return { active: true, all: mode === "all", partial: mode === "partial" };
}

/** 通信失敗を表すエラー（メッセージは開発用） */
export class SimulatedNetworkError extends Error {
  constructor(target: FailureTarget) {
    super(`[開発用] 通信失敗をシミュレートしました（${target}）`);
    this.name = "SimulatedNetworkError";
  }
}

/**
 * 一部失敗モードで「このIDを失敗させる」かを判定する。
 * 2件以上なら必ず一部だけが失敗し、1件だけのときはその1件が失敗する
 * （「失敗したのか分からない」状態を作らないため）。
 */
export function shouldFailId(index: number, total: number): boolean {
  if (total <= 1) return true;
  return index % 2 === 1;
}